import { useEffect, useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { supabase } from '@/lib/supabase';
import { Card, Button, TextInput, Badge, Spinner, Modal, Label, Select } from 'flowbite-react';
import {
  HiPlus,
  HiPencil,
  HiTrash,
  HiRefresh,
  HiSearch,
  HiCheckCircle,
  HiXCircle,
  HiInformationCircle,
  HiExclamationCircle,
  HiDeviceMobile,
  HiX,
} from 'react-icons/hi';
import AdminLayout from '@/components/layout/AdminLayout';
import Pagination from '@/components/Pagination';

interface Device {
  id: string;
  name: string;
  device_id: string;
  type: string;
  tenant_id: string;
  mqtt_topic: string;
  status: 'online' | 'offline' | 'error';
  last_seen: string | null;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

export default function AdminDevices() {
  const { userProfile } = useAuth();
  const [devices, setDevices] = useState<Device[]>([]);
  const [filteredDevices, setFilteredDevices] = useState<Device[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [showEditModal, setShowEditModal] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [editingDevice, setEditingDevice] = useState<Device | null>(null);
  const [confirmAction, setConfirmAction] = useState<(() => void) | null>(null);
  const [confirmMessage, setConfirmMessage] = useState('');
  const [messageModal, setMessageModal] = useState({ type: 'success' as 'success' | 'error' | 'info', title: '', message: '', show: false });
  
  // Estados de paginación
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [displayedDevices, setDisplayedDevices] = useState<Device[]>([]);
  const [formData, setFormData] = useState({
    name: '',
    device_id: '',
    type: '',
    tenant_id: '',
    mqtt_topic: '',
    status: 'offline' as 'online' | 'offline' | 'error',
  });
  const [tenants, setTenants] = useState<Array<{ id: string; name: string }>>([]);

  const loadTenants = async () => {
    const { data } = await supabase
      .from('tenants')
      .select('id, name')
      .eq('status', 'active');
    if (data) setTenants(data);
  };

  const loadDevices = async () => {
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('devices')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) throw error;
      setDevices(data || []);
      setFilteredDevices(data || []);
    } catch (error: any) {
      console.error('Error loading devices:', error);
      showMessage('error', 'Error', 'Error al cargar dispositivos: ' + (error.message || 'Error desconocido'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (userProfile?.role === 'superadmin') {
      loadTenants();
      loadDevices();
    }
  }, [userProfile]);

  useEffect(() => {
    let filtered = devices;
    if (searchTerm) {
      filtered = filtered.filter(
        (device) =>
          device.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
          device.device_id.toLowerCase().includes(searchTerm.toLowerCase()) ||
          device.mqtt_topic.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }
    setFilteredDevices(filtered);
    setCurrentPage(1); // Resetear a la primera página cuando cambia el filtro
  }, [searchTerm, devices]);

  // Calcular dispositivos a mostrar según paginación
  useEffect(() => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    setDisplayedDevices(filteredDevices.slice(startIndex, endIndex));
  }, [filteredDevices, currentPage, itemsPerPage]);

  const showMessage = (type: 'success' | 'error' | 'info', title: string, message: string) => {
    setMessageModal({ type, title, message, show: true });
  };

  const showConfirm = (message: string, action: () => void) => {
    setConfirmMessage(message);
    setConfirmAction(() => action);
    setShowConfirmModal(true);
  };

  const openEditModal = (device?: Device) => {
    if (device) {
      setEditingDevice(device);
      setFormData({
        name: device.name || '',
        device_id: device.device_id || '',
        type: device.type || '',
        tenant_id: device.tenant_id || '',
        mqtt_topic: device.mqtt_topic || '',
        status: device.status || 'offline',
      });
    } else {
      setEditingDevice(null);
      setFormData({
        name: '',
        device_id: '',
        type: '',
        tenant_id: tenants[0]?.id || '',
        mqtt_topic: '',
        status: 'offline',
      });
    }
    setShowEditModal(true);
  };

  const closeEditModal = () => {
    setShowEditModal(false);
    setEditingDevice(null);
  };

  const saveDevice = async () => {
    try {
      if (editingDevice) {
        const { error } = await supabase
          .from('devices')
          .update({
            name: formData.name,
            type: formData.type,
            mqtt_topic: formData.mqtt_topic,
            status: formData.status,
            is_active: formData.status !== 'error',
          })
          .eq('id', editingDevice.id);

        if (error) throw error;
        showMessage('success', 'Dispositivo Actualizado', 'El dispositivo ha sido actualizado exitosamente');
      } else {
        const { error } = await supabase
          .from('devices')
          .insert({
            name: formData.name,
            device_id: formData.device_id,
            type: formData.type,
            tenant_id: formData.tenant_id,
            mqtt_topic: formData.mqtt_topic,
            status: formData.status,
            is_active: formData.status !== 'error',
          });

        if (error) throw error;
        showMessage('success', 'Dispositivo Creado', 'El dispositivo ha sido creado exitosamente');
      }
      closeEditModal();
      await loadDevices();
    } catch (error: any) {
      console.error('Error saving device:', error);
      showMessage('error', 'Error', 'Error al guardar dispositivo: ' + (error.message || 'Error desconocido'));
    }
  };

  const deleteDevice = async (device: Device) => {
    showConfirm(
      `¿Estás seguro de que deseas eliminar el dispositivo "${device.name}"? Esta acción no se puede deshacer.`,
      async () => {
        try {
          const { error } = await supabase
            .from('devices')
            .delete()
            .eq('id', device.id);

          if (error) throw error;
          showMessage('success', 'Dispositivo Eliminado', 'El dispositivo ha sido eliminado exitosamente');
          await loadDevices();
        } catch (error: any) {
          console.error('Error deleting device:', error);
          showMessage('error', 'Error', 'Error al eliminar dispositivo: ' + (error.message || 'Error desconocido'));
        } finally {
          setShowConfirmModal(false);
        }
      }
    );
  };

  const getStatusBadge = (status: string) => {
    const config = {
      online: { color: 'success' as const, label: 'En Línea' },
      offline: { color: 'gray' as const, label: 'Desconectado' },
      error: { color: 'failure' as const, label: 'Error' },
    };
    const statusConfig = config[status as keyof typeof config] || config.offline;
    return <Badge color={statusConfig.color}>{statusConfig.label}</Badge>;
  };

  if (!userProfile || userProfile.role !== 'superadmin') {
    return (
      <AdminLayout>
        <Card>
          <div className="p-6">
            <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-2">Acceso Denegado</h3>
            <p className="text-gray-600 dark:text-gray-400">No tienes permisos para acceder a esta página</p>
          </div>
        </Card>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="space-y-6">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 dark:text-white">Gestión de Dispositivos</h1>
            <p className="text-gray-600 dark:text-gray-400 mt-1">Administra los dispositivos del sistema</p>
          </div>
          <Button color="blue" onClick={() => openEditModal()} className="gap-2">
            <HiPlus className="w-4 h-4" />
            Nuevo Dispositivo
          </Button>
        </div>

        {/* Estadísticas - Compactas */}
        <div className="grid grid-cols-3 gap-3">
          <Card className="bg-white dark:bg-gray-800 py-2 px-3">
            <div className="flex items-center justify-between">
              <div className="flex-1 min-w-0">
                <p className="text-xs text-gray-600 dark:text-gray-400 truncate">Total</p>
                <p className="text-lg font-bold text-gray-900 dark:text-white">{devices.length}</p>
              </div>
              <HiDeviceMobile className="w-5 h-5 text-blue-600 flex-shrink-0 ml-2" />
            </div>
          </Card>
          <Card className="bg-white dark:bg-gray-800 py-2 px-3">
            <div className="flex items-center justify-between">
              <div className="flex-1 min-w-0">
                <p className="text-xs text-gray-600 dark:text-gray-400 truncate">En Línea</p>
                <p className="text-lg font-bold text-green-600">
                  {devices.filter((d) => d.status === 'online').length}
                </p>
              </div>
              <HiCheckCircle className="w-5 h-5 text-green-600 flex-shrink-0 ml-2" />
            </div>
          </Card>
          <Card className="bg-white dark:bg-gray-800 py-2 px-3">
            <div className="flex items-center justify-between">
              <div className="flex-1 min-w-0">
                <p className="text-xs text-gray-600 dark:text-gray-400 truncate">Desconectados</p>
                <p className="text-lg font-bold text-red-600">
                  {devices.filter((d) => d.status === 'offline' || d.status === 'error').length}
                </p>
              </div>
              <HiX className="w-5 h-5 text-red-600 flex-shrink-0 ml-2" />
            </div>
          </Card>
        </div>

        {/* Búsqueda y filtros */}
        <Card className="bg-white dark:bg-gray-800">
          <div className="p-6">
            <div className="flex flex-col sm:flex-row gap-4">
              <div className="flex-1 relative">
                <TextInput
                  type="text"
                  placeholder="Buscar por nombre, ID o MQTT topic..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10"
                />
                <HiSearch className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400 pointer-events-none" />
              </div>
              <div className="flex gap-2">
                <Select
                  value={itemsPerPage.toString()}
                  onChange={(e) => {
                    setItemsPerPage(parseInt(e.target.value));
                    setCurrentPage(1);
                  }}
                  className="w-32"
                >
                  <option value="10">10 por página</option>
                  <option value="25">25 por página</option>
                  <option value="50">50 por página</option>
                  <option value="100">100 por página</option>
                </Select>
                <Button color="gray" onClick={loadDevices} size="sm" className="gap-2 text-gray-900 dark:text-gray-200 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600">
                  <HiRefresh className="w-4 h-4" />
                </Button>
              </div>
            </div>
          </div>
        </Card>

        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Spinner size="xl" className="text-blue-600" />
          </div>
        ) : filteredDevices.length === 0 ? (
          <Card className="bg-white dark:bg-gray-800">
            <div className="py-12 text-center">
              <p className="text-gray-600 dark:text-gray-400 text-lg">No se encontraron dispositivos</p>
            </div>
          </Card>
        ) : (
          <>
            {/* Vista de Tabla - Desktop */}
            <Card className="bg-white dark:bg-gray-800 hidden lg:block">
              <div className="overflow-x-auto">
                <table className="w-full text-left text-sm text-gray-500 dark:text-gray-400">
                  <thead className="bg-gray-50 text-xs uppercase text-gray-700 dark:bg-gray-700 dark:text-gray-400">
                    <tr>
                      <th scope="col" className="px-6 py-3">Nombre</th>
                      <th scope="col" className="px-6 py-3">Device ID</th>
                      <th scope="col" className="px-6 py-3">Tipo</th>
                      <th scope="col" className="px-6 py-3">MQTT Topic</th>
                      <th scope="col" className="px-6 py-3">Estado</th>
                      <th scope="col" className="px-6 py-3">Última Conexión</th>
                      <th scope="col" className="px-6 py-3">Acciones</th>
                    </tr>
                  </thead>
                  <tbody>
                    {displayedDevices.map((device) => (
                    <tr key={device.id} className="border-b bg-white dark:border-gray-700 dark:bg-gray-800">
                      <td className="whitespace-nowrap px-6 py-4 font-medium text-gray-900 dark:text-white">{device.name}</td>
                      <td className="px-6 py-4 font-mono text-xs">{device.device_id}</td>
                      <td className="px-6 py-4">{device.type}</td>
                      <td className="px-6 py-4 font-mono text-xs">{device.mqtt_topic}</td>
                      <td className="px-6 py-4">{getStatusBadge(device.status)}</td>
                      <td className="px-6 py-4">
                        {device.last_seen
                          ? new Date(device.last_seen).toLocaleString('es-ES')
                          : 'Nunca'}
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex gap-2">
                          <Button
                            onClick={() => openEditModal(device)}
                            size="sm"
                            color="gray"
                            className="gap-1 text-gray-900 dark:text-gray-200 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600"
                          >
                            <HiPencil className="w-3 h-3" />
                            Editar
                          </Button>
                          <Button
                            onClick={() => deleteDevice(device)}
                            size="sm"
                            color="failure"
                            className="gap-1"
                          >
                            <HiTrash className="w-3 h-3" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </Card>

            {/* Vista de Tarjetas - Móvil */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 lg:hidden">
              {displayedDevices.map((device) => (
                <Card key={device.id} className="hover:shadow-lg transition-shadow bg-white dark:bg-gray-800">
                  <div className="p-4">
                    <div className="flex items-start justify-between mb-3">
                      <div className="flex-1">
                        <h3 className="text-lg font-bold text-gray-900 dark:text-white">{device.name}</h3>
                        <p className="text-xs font-mono text-gray-500 dark:text-gray-400 mt-1">{device.device_id}</p>
                      </div>
                      <div className="flex flex-col gap-1 ml-2">
                        {getStatusBadge(device.status)}
                      </div>
                    </div>
                    <div className="space-y-1 text-sm mb-3">
                      <div className="text-gray-600 dark:text-gray-400">
                        <strong className="text-gray-900 dark:text-white">Tipo:</strong> {device.type}
                      </div>
                      <div className="text-gray-600 dark:text-gray-400">
                        <strong className="text-gray-900 dark:text-white">MQTT Topic:</strong>{' '}
                        <span className="font-mono text-xs">{device.mqtt_topic}</span>
                      </div>
                      <div className="text-gray-600 dark:text-gray-400">
                        <strong className="text-gray-900 dark:text-white">Última Conexión:</strong>{' '}
                        {device.last_seen ? new Date(device.last_seen).toLocaleString('es-ES') : 'Nunca'}
                      </div>
                      <div className="text-gray-500 dark:text-gray-400 text-xs">
                        Creado: {new Date(device.created_at).toLocaleDateString('es-ES')}
                      </div>
                    </div>
                    <div className="flex flex-wrap gap-2 pt-3 border-t border-gray-200 dark:border-gray-700">
                      <Button
                        onClick={() => openEditModal(device)}
                        size="sm"
                        color="gray"
                        className="flex-1 gap-1 text-gray-900 dark:text-gray-200 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600"
                      >
                        <HiPencil className="w-3 h-3" />
                        Editar
                      </Button>
                      <Button
                        onClick={() => deleteDevice(device)}
                        size="sm"
                        color="failure"
                        className="gap-1"
                      >
                        <HiTrash className="w-3 h-3" />
                      </Button>
                    </div>
                  </div>
                </Card>
              ))}
            </div>

            {/* Paginación */}
            {filteredDevices.length > itemsPerPage && (
              <Card className="bg-white dark:bg-gray-800 p-0">
                <Pagination
                  currentPage={currentPage}
                  totalPages={Math.ceil(filteredDevices.length / itemsPerPage)}
                  onPageChange={(page) => setCurrentPage(page)}
                  totalItems={filteredDevices.length}
                  itemsPerPage={itemsPerPage}
                  showInfo={true}
                />
              </Card>
            )}
          </>
        )}

        {/* Modal de Edición */}
        <Modal show={showEditModal} onClose={closeEditModal} size="md">
          <div className="p-6 bg-white dark:bg-gray-800">
            <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">
              {editingDevice ? 'Editar Dispositivo' : 'Nuevo Dispositivo'}
            </h3>
            <div className="space-y-4">
              <div>
                <Label htmlFor="name">Nombre</Label>
                <TextInput
                  id="name"
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="mt-1"
                  required
                />
              </div>
              {!editingDevice && (
                <>
                  <div>
                    <Label htmlFor="device_id">Device ID</Label>
                    <TextInput
                      id="device_id"
                      type="text"
                      value={formData.device_id}
                      onChange={(e) => setFormData({ ...formData, device_id: e.target.value })}
                      className="mt-1"
                      required
                    />
                  </div>
                  <div>
                    <Label htmlFor="tenant_id">Tenant</Label>
                    <select
                      id="tenant_id"
                      value={formData.tenant_id}
                      onChange={(e) => setFormData({ ...formData, tenant_id: e.target.value })}
                      className="mt-1 block w-full rounded-lg border border-gray-300 bg-gray-50 p-2.5 text-sm text-gray-900 focus:border-blue-500 focus:ring-blue-500 dark:border-gray-600 dark:bg-gray-700 dark:text-white dark:placeholder-gray-400 dark:focus:border-blue-500 dark:focus:ring-blue-500"
                      required
                    >
                      <option value="">Seleccionar tenant</option>
                      {tenants.map((tenant) => (
                        <option key={tenant.id} value={tenant.id}>
                          {tenant.name}
                        </option>
                      ))}
                    </select>
                  </div>
                </>
              )}
              <div>
                <Label htmlFor="type">Tipo</Label>
                <TextInput
                  id="type"
                  type="text"
                  value={formData.type}
                  onChange={(e) => setFormData({ ...formData, type: e.target.value })}
                  className="mt-1"
                  required
                />
              </div>
              <div>
                <Label htmlFor="mqtt_topic">MQTT Topic</Label>
                <TextInput
                  id="mqtt_topic"
                  type="text"
                  value={formData.mqtt_topic}
                  onChange={(e) => setFormData({ ...formData, mqtt_topic: e.target.value })}
                  className="mt-1"
                  required
                />
              </div>
              <div>
                <Label htmlFor="status">Estado</Label>
                <select
                  id="status"
                  value={formData.status}
                  onChange={(e) => setFormData({ ...formData, status: e.target.value as 'online' | 'offline' | 'error' })}
                  className="mt-1 block w-full rounded-lg border border-gray-300 bg-gray-50 p-2.5 text-sm text-gray-900 focus:border-blue-500 focus:ring-blue-500 dark:border-gray-600 dark:bg-gray-700 dark:text-white dark:placeholder-gray-400 dark:focus:border-blue-500 dark:focus:ring-blue-500"
                >
                  <option value="online">En Línea</option>
                  <option value="offline">Desconectado</option>
                  <option value="error">Error</option>
                </select>
              </div>
            </div>
            <div className="flex justify-end gap-3 mt-6 pt-4 border-t border-gray-200 dark:border-gray-700">
              <Button color="blue" onClick={saveDevice}>
                {editingDevice ? 'Guardar Cambios' : 'Crear Dispositivo'}
              </Button>
              <Button color="gray" onClick={closeEditModal} className="text-gray-900 dark:text-gray-200 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600">
                Cancelar
              </Button>
            </div>
          </div>
        </Modal>

        {/* Modal de Confirmación */}
        <Modal show={showConfirmModal} onClose={() => {
          setShowConfirmModal(false);
          setConfirmAction(null);
          setConfirmMessage('');
        }} size="md">
          <div className="p-6 bg-white dark:bg-gray-800 text-center">
            <HiExclamationCircle className="mx-auto mb-4 h-12 w-12 text-red-600 dark:text-red-400" />
            <h3 className="mb-5 text-lg font-normal text-gray-900 dark:text-gray-200">
              {confirmMessage || (confirmAction ? '¿Estás seguro de realizar esta acción?' : '')}
            </h3>
            <div className="flex justify-center gap-4">
              <Button
                color="failure"
                onClick={() => {
                  if (confirmAction) confirmAction();
                  setShowConfirmModal(false);
                  setConfirmAction(null);
                  setConfirmMessage('');
                }}
                className="bg-red-600 hover:bg-red-700 text-white dark:bg-red-600 dark:hover:bg-red-700 dark:text-white font-semibold px-6"
              >
                Sí, estoy seguro
              </Button>
              <Button
                color="gray"
                onClick={() => {
                  setShowConfirmModal(false);
                  setConfirmAction(null);
                  setConfirmMessage('');
                }}
                className="text-gray-900 dark:text-gray-200 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600"
              >
                No, cancelar
              </Button>
            </div>
          </div>
        </Modal>

        {/* Modal de Mensaje */}
        <Modal show={messageModal.show} onClose={() => setMessageModal({ ...messageModal, show: false })} size="md">
          <div className="p-6 bg-white dark:bg-gray-800 text-center">
            {messageModal.type === 'success' && <HiCheckCircle className="mx-auto mb-4 h-12 w-12 text-green-500" />}
            {messageModal.type === 'error' && <HiXCircle className="mx-auto mb-4 h-12 w-12 text-red-500" />}
            {messageModal.type === 'info' && <HiInformationCircle className="mx-auto mb-4 h-12 w-12 text-blue-500" />}
            <h3 className="mb-2 text-xl font-bold text-gray-900 dark:text-white">{messageModal.title}</h3>
            <p className="mb-5 text-base text-gray-500 dark:text-gray-400">{messageModal.message}</p>
            <Button color="blue" onClick={() => setMessageModal({ ...messageModal, show: false })}>
              Cerrar
            </Button>
          </div>
        </Modal>
      </div>
    </AdminLayout>
  );
}
